import { DialogObject } from '@/components/dialog/dialog.types';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { DynamicField, DynamicForm, FieldVariant, PasswordFieldProps } from '@/components/form-builder/form-builder.types';
import { DacRepository } from '@/store/dac-state/dac-state.repository';

interface GetDacPasswordDialogProps {
  store: DacRepository;
  specialValidate: (password: string) => void;
  passwordRequired?: boolean;
  dialogTitle?: string;
  storeKey?: string;
}

export const getDacPasswordDialog = ({
  store,
  specialValidate,
  passwordRequired = false,
  dialogTitle = 'Mot de passe requis',
  storeKey = 'passwordDialog',
}: GetDacPasswordDialogProps): DialogObject | null => {
  // If password is not required, no dialog needed - caller should call validate() directly
  if (!passwordRequired) {
    return null;
  }

  // Password required: show simple password form dialog
  const passwordField: DynamicField<PasswordFieldProps> = {
    label: 'Mot de passe',
    variant: FieldVariant.PASSWORD,
    isRequired: true,
    error: store.getNestedObservable<string>('error', 'password'),
    props: {
      value: store.getNestedObservable<string>(storeKey, 'password'),
      onChange: (value: string) => {
        store.setNested(storeKey, 'password', value);
        store.setNested('error', 'password', '');
      },
      placeholder: 'Mot de passe',
      message: 'common.password.warn',
    },
  };

  const confirmPasswordField: DynamicField<PasswordFieldProps> = {
    label: 'Confirmer le mot de passe',
    variant: FieldVariant.PASSWORD,
    isRequired: true,
    error: store.getNestedObservable<string>('error', 'confirmPassword'),
    props: {
      value: store.getNestedObservable<string>(storeKey, 'confirmPassword'),
      onChange: (value: string) => {
        store.setNested(storeKey, 'confirmPassword', value);
        store.setNested('error', 'confirmPassword', '');
      },
      placeholder: 'Confirmer le mot de passe',
      message: 'common.confirmPassword.warn',
    },
  };

  const passwordFormObject: DynamicForm = {
    title: '',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [{ fields: [passwordField] }, { fields: [confirmPasswordField] }],
      },
    ],
  };

  return {
    title: dialogTitle,
    width: '40vw',
    actions: [
      {
        label: 'dac.actions.validate',
        variant: 'success',
        onClick: () => {
          const password = store.getNested<string>(storeKey, 'password');
          if (!password) {
            store.setNested('error', 'password', 'Veuillez saisir un mot de passe');
            return;
          }
          const confirmPassword = store.getNested<string>(storeKey, 'confirmPassword');
          if (!confirmPassword) {
            store.setNested('error', 'confirmPassword', 'Veuillez confirmer le mot de passe');
            return;
          }
          if (password !== confirmPassword) {
            store.setNested('error', 'confirmPassword', 'Les mots de passe ne correspondent pas');
            return;
          }
          store.setNested('error', 'password', '');
          store.setNested('error', 'confirmPassword', '');
          specialValidate(password);
        },
      },
    ],
    component: {
      outlet: FormBuilderComponent,
      props: { formObject: passwordFormObject },
    },
  };
};
